import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardBody,
  Button,
  Spinner,
  Alert,
  Badge
} from 'reactstrap';
import { FaExternalLinkAlt, FaRedo, FaChartLine, FaDatabase, FaClock, FaInfoCircle } from 'react-icons/fa';

const PACProgramado = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const handleIframeLoad = () => {
    setLoading(false);
    setConnectionStatus('connected');
    setLastUpdated(new Date());
  };

  const handleIframeError = () => {
    setLoading(false);
    setError(true);
    setConnectionStatus('error');
  };

  const openInNewTab = () => {
    window.open('https://lookerstudio.google.com/embed/u/0/reporting/c4a83ecb-9dab-4fdb-a6e7-cf873bbb9140/page/ciAUF', '_blank');
  };

  const refreshDashboard = () => {
    setLoading(true);
    setError(false);
    setConnectionStatus('connecting');
    
    // Recargar el iframe
    const iframe = document.getElementById('looker-iframe');
    if (iframe) {
      iframe.src = iframe.src;
    }
  };

  // Simulador de tiempo transcurrido desde la última actualización
  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdated(prev => prev);
    }, 60000); // Actualizar cada minuto

    return () => clearInterval(interval);
  }, []);

  const getTimeSinceUpdate = () => {
    const now = new Date();
    const diff = Math.floor((now - lastUpdated) / 1000 / 60);
    if (diff < 1) return 'Hace menos de 1 minuto';
    if (diff === 1) return 'Hace 1 minuto';
    return `Hace ${diff} minutos`;
  };

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'success';
      case 'error': return 'danger';
      default: return 'warning';
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected': return 'Conectado';
      case 'error': return 'Error de conexión';
      default: return 'Conectando...';
    }
  };

  return (
    <div className="dashboard-container">
      {/* Header mejorado */}
      <div className="dashboard-header">
        <Container fluid>
          <div className="d-flex justify-content-between align-items-center py-3">
            <div className="dashboard-title-section">
              <h2 className="dashboard-title mb-2">
                <FaChartLine className="me-2 text-primary" />
                Dashboard PAC Programado 2025
              </h2>
              <div className="d-flex align-items-center gap-3">
                <Badge color={getStatusColor()} className="status-badge">
                  <FaDatabase className="me-1" size={12} />
                  {getStatusText()}
                </Badge>
                {connectionStatus === 'connected' && (
                  <small className="text-muted">
                    <FaClock className="me-1" />
                    Actualizado: {getTimeSinceUpdate()}
                  </small>
                )}
              </div>
            </div>
            
            <div className="dashboard-actions">
              <Button
                color="outline-secondary"
                size="sm"
                onClick={refreshDashboard}
                disabled={loading}
                className="me-2 action-btn"
              >
                <FaRedo className={`me-2 ${loading ? 'fa-spin' : ''}`} />
                Actualizar
              </Button>
              <Button
                color="primary"
                size="sm"
                onClick={openInNewTab}
                className="action-btn"
              >
                <FaExternalLinkAlt className="me-2" />
                Nueva Pestaña
              </Button>
            </div>
          </div>
        </Container>
      </div>

      {/* Alertas de error mejoradas */}
      {error && (
        <Container fluid className="px-3">
          <Alert color="danger" className="error-alert">
            <div className="d-flex align-items-start">
              <FaInfoCircle className="me-3 mt-1 text-danger" size={20} />
              <div className="flex-grow-1">
                <h6 className="alert-heading fw-bold mb-2">Error de Conexión</h6>
                <p className="mb-3">
                  No se pudo establecer conexión con Looker Studio. Esto puede deberse a:
                </p>
                <ul className="mb-3">
                  <li>Problemas de conectividad a internet</li>
                  <li>Mantenimiento temporal del servicio</li>
                  <li>Restricciones de red corporativa</li>
                </ul>
                <div className="d-flex gap-2">
                  <Button 
                    color="danger" 
                    size="sm" 
                    onClick={refreshDashboard}
                    className="fw-bold"
                  >
                    <FaRedo className="me-2" />
                    Reintentar Conexión
                  </Button>
                  <Button 
                    color="outline-danger" 
                    size="sm" 
                    onClick={openInNewTab}
                  >
                    <FaExternalLinkAlt className="me-2" />
                    Abrir en Nueva Pestaña
                  </Button>
                </div>
              </div>
            </div>
          </Alert>
        </Container>
      )}

      {/* Contenedor principal del dashboard */}
      <div className="dashboard-content">
        <Container fluid className="h-100">
          <div className="dashboard-frame-container">
            {/* Indicador de carga mejorado */}
            {loading && (
              <div className="loading-overlay">
                <div className="loading-content">
                  <div className="loading-spinner-container">
                    <Spinner 
                      color="primary" 
                      style={{ width: '4rem', height: '4rem' }} 
                      className="mb-4" 
                    />
                    <div className="loading-progress">
                      <div className="progress-bar"></div>
                    </div>
                  </div>
                  <h4 className="loading-title">Cargando Dashboard Interactivo</h4>
                  <p className="loading-subtitle">
                    Estableciendo conexión segura con Looker Studio...
                  </p>
                  <div className="loading-tips">
                    <small className="text-muted">
                      💡 <strong>Tip:</strong> Para mejor rendimiento, usa el botón "Nueva Pestaña"
                    </small>
                  </div>
                </div>
              </div>
            )}

            {/* Iframe del dashboard */}
            <iframe
              id="looker-iframe"
              src="https://lookerstudio.google.com/embed/u/0/reporting/c4a83ecb-9dab-4fdb-a6e7-cf873bbb9140/page/ciAUF"
              className="dashboard-iframe"
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              title="Dashboard PAC Programado 2025 - Análisis Interactivo"
              allowFullScreen
              style={{ opacity: loading ? 0 : 1 }}
            />
          </div>
        </Container>
      </div>

      {/* Información adicional */}
      {!loading && !error && (
        <div className="dashboard-footer">
          <Container fluid>
            <div className="d-flex justify-content-between align-items-center py-2">
              <small className="text-muted">
                <FaDatabase className="me-1" />
                Datos actualizados en tiempo real desde fuentes oficiales
              </small>
              <small className="text-muted">
                Dashboard optimizado para análisis del PAC 2025
              </small>
            </div>
          </Container>
        </div>
      )}

      {/* Estilos CSS */}
      <style jsx>{`
        .dashboard-container {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          min-height: 100vh;
          display: flex;
          flex-direction: column;
        }

        .dashboard-header {
          background: white;
          box-shadow: 0 2px 20px rgba(0, 0, 0, 0.08);
          border-bottom: 1px solid #e9ecef;
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .dashboard-title {
          color: #2c3e50;
          font-weight: 700;
          font-size: 1.75rem;
          margin: 0;
        }

        .status-badge {
          font-size: 0.85rem;
          padding: 0.4rem 0.8rem;
          border-radius: 1rem;
          font-weight: 600;
        }

        .action-btn {
          border-radius: 0.75rem;
          font-weight: 600;
          padding: 0.5rem 1rem;
          transition: all 0.3s ease;
          border-width: 2px;
        }

        .action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .error-alert {
          border-radius: 1rem;
          border: none;
          box-shadow: 0 4px 20px rgba(220, 53, 69, 0.15);
          margin: 1rem 0;
        }

        .dashboard-content {
          flex: 1;
          padding: 1.5rem 0;
          position: relative;
        }

        .dashboard-frame-container {
          position: relative;
          height: calc(100vh - 200px);
          min-height: 600px;
          background: white;
          border-radius: 1.5rem;
          overflow: hidden;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(0, 0, 0, 0.05);
        }

        .dashboard-iframe {
          width: 100%;
          height: 100%;
          border: none;
          transition: opacity 0.5s ease;
        }

        .loading-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 249, 250, 0.95) 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10;
          backdrop-filter: blur(10px);
        }

        .loading-content {
          text-align: center;
          max-width: 400px;
          padding: 2rem;
        }

        .loading-spinner-container {
          position: relative;
          margin-bottom: 2rem;
        }

        .loading-progress {
          width: 200px;
          height: 4px;
          background: #e9ecef;
          border-radius: 2px;
          overflow: hidden;
          margin: 1rem auto 0;
        }

        .progress-bar {
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, #007bff, #0056b3);
          border-radius: 2px;
          animation: loading-progress 2s ease-in-out infinite;
        }

        @keyframes loading-progress {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(0%); }
          100% { transform: translateX(100%); }
        }

        .loading-title {
          color: #2c3e50;
          font-weight: 700;
          margin-bottom: 1rem;
          font-size: 1.5rem;
        }

        .loading-subtitle {
          color: #6c757d;
          margin-bottom: 1.5rem;
          font-size: 1.1rem;
          line-height: 1.5;
        }

        .loading-tips {
          background: linear-gradient(135deg, #e3f2fd, #f3e5f5);
          padding: 1rem;
          border-radius: 0.75rem;
          border: 1px solid #bbdefb;
        }

        .dashboard-footer {
          background: white;
          border-top: 1px solid #e9ecef;
          margin-top: auto;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .dashboard-header .d-flex {
            flex-direction: column;
            gap: 1rem;
          }

          .dashboard-actions {
            width: 100%;
            display: flex;
            justify-content: center;
          }

          .dashboard-title {
            font-size: 1.5rem;
            text-align: center;
          }

          .dashboard-frame-container {
            height: calc(100vh - 250px);
            min-height: 500px;
            border-radius: 1rem;
            margin: 0 0.5rem;
          }

          .loading-content {
            padding: 1rem;
          }

          .loading-title {
            font-size: 1.25rem;
          }

          .loading-subtitle {
            font-size: 1rem;
          }
        }

        @media (max-width: 576px) {
          .dashboard-content {
            padding: 1rem 0;
          }

          .dashboard-frame-container {
            height: calc(100vh - 280px);
            min-height: 400px;
            margin: 0;
            border-radius: 0.75rem;
          }

          .action-btn {
            font-size: 0.875rem;
            padding: 0.4rem 0.8rem;
          }

          .dashboard-footer .d-flex {
            flex-direction: column;
            text-align: center;
            gap: 0.5rem;
          }
        }

        /* Animaciones */
        .dashboard-header {
          animation: slideInDown 0.6s ease-out;
        }

        .dashboard-frame-container {
          animation: fadeInUp 0.8s ease-out;
        }

        @keyframes slideInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Estados de hover mejorados */
        .status-badge {
          transition: all 0.3s ease;
        }

        .status-badge:hover {
          transform: scale(1.05);
        }

        /* Mejoras de accesibilidad */
        .action-btn:focus {
          outline: 3px solid rgba(0, 123, 255, 0.3);
          outline-offset: 2px;
        }

        .dashboard-iframe:focus {
          outline: 3px solid rgba(0, 123, 255, 0.3);
          outline-offset: 4px;
        }
      `}</style>
    </div>
  );
};

export default PACProgramado;